import React, { useState, useEffect, useRef } from 'react';
import { getQuestions, submitAnswer, getHints } from '../../services/learningService';
import { evaluateAnswer, getHeartLessonEvaluationConfig } from '../../services/answerEvaluator';
import {
  PageContainer,
  NavBar,
  NavButton,
  LogoContainer,
  MainContent,
  ContentContainer,
  SectionTitle,
  ProgressBarContainer,
  ProgressBarWrapper,
  ProgressBarHeader,
  ProgressBarTrack,
  ProgressBarIndicator,
  HeartIconContainer,
  AnimatedHeart,
  WelcomeText,
  InstructionCard,
  ActionButton,
  VoiceInputContainer,
  MicButton,
  MicIcon,
  VoiceInputButton,
  ResponseCard,
  ResponseHeader,
  CheckIconWrapper,
  ResponseContent,
  HeartRateContainer,
  HeartRateHeader,
  ActionButtons,
  ExerciseButton,
  RestButton,
  HeartModelContainer,
  LessonContainer,
  QuestionText,
  AnswerButton,
  FeedbackText,
  HintButton,
  HintContainer
} from './HeartLessonStyles';
import { Book, ArrowLeft, ArrowRight, Mic, Info, Check, X } from 'lucide-react';
import InteractiveHeartModel from './InteractiveHeartModel';

// Add type definition for SpeechRecognition
declare global {
  interface Window {
    webkitSpeechRecognition: new () => SpeechRecognition;
  }
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  onstart: (() => void) | null;
  onresult: ((event: any) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: any) => void) | null;
}

const SESSION_ID = 'user-1-heart-session';

const HeartLesson = () => {
  const [questions, setQuestions] = useState<any[]>([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [feedback, setFeedback] = useState('');
  const [hints, setHints] = useState<any[]>([]);
  const [showHint, setShowHint] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [answerResult, setAnswerResult] = useState<{ correct: boolean, text: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [transcript, setTranscript] = useState('');
  const [showCompletion, setShowCompletion] = useState(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const finalTranscriptRef = useRef('');
  const [showModel, setShowModel] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isQuestionsReady, setIsQuestionsReady] = useState(false);
  const pendingVoiceSubmission = useRef<string | null>(null);
  const initializationInProgress = useRef(false);
  const questionsLoaded = useRef(false);
  const [pendingVoiceText, setPendingVoiceText] = useState('');

  // Process voice submission
  const processVoiceSubmission = async (voiceText: string) => {
    console.log('Processing voice submission:', voiceText);
    if (!voiceText.trim()) {
      console.log('Empty voice text, not processing');
      return;
    }

    try {
      const question = questions[currentStep];
      console.log('Processing state:', {
        currentStep,
        questionId: question?.id,
        questionText: question?.text,
        voiceText
      });
      
      if (!question) {
        console.error('No question found for current step:', currentStep);
        return;
      }
      
      // Get evaluation config and evaluate answer
      const config = getHeartLessonEvaluationConfig(question.id);
      if (!config) {
        console.error('No evaluation config found for question:', question.id);
        return;
      }
      const evaluation = evaluateAnswer(question.id, voiceText, config);
      console.log('Answer evaluation:', evaluation);
      
      setFeedback(evaluation.feedback);
      setAnswerResult({ correct: evaluation.correct, text: voiceText });
      setTranscript('');
    } catch (error) {
      console.error('Error processing voice submission:', error);
      setFeedback('Error evaluating your answer. Please try again.');
    }
  };

  // Initialize speech recognition
  const initializeSpeechRecognition = () => {
    if ('webkitSpeechRecognition' in window) {
      const recognition = new (window as any).webkitSpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      recognition.maxAlternatives = 1;

      recognition.onstart = () => {
        if (!questionsLoaded.current) {
          console.log('Questions not loaded yet, stopping recognition');
          recognition.stop();
          return;
        }
        console.log('Voice recognition started');
        setIsListening(true);
        setTranscript('');
        finalTranscriptRef.current = '';
        setPendingVoiceText('');
      };

      recognition.onresult = (event: any) => {
        const transcript = Array.from(event.results)
          .map((result: any) => result[0])
          .map(result => result.transcript)
          .join('');
        
        if (transcript.trim()) {
          console.log('Transcript:', transcript);
          setTranscript(transcript);
          finalTranscriptRef.current = transcript;
          setPendingVoiceText(transcript);
        }
      };

      recognition.onend = () => {
        console.log('Voice recognition ended');
        setIsListening(false);
      };

      recognition.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        
        switch (event.error) {
          case 'no-speech':
            setFeedback('No speech detected. Please try again.');
            break;
          case 'audio-capture':
            setFeedback('No microphone detected. Please check your microphone settings.');
            break;
          case 'not-allowed':
            setFeedback('Microphone access denied. Please allow microphone access.');
            break;
          default:
            setFeedback('Error with voice recognition. Please try again.');
        }
      };

      recognitionRef.current = recognition;
    }
  };

  useEffect(() => {
    const initialize = async () => {
      // Prevent multiple initializations
      if (initializationInProgress.current) {
        console.log('Initialization already in progress');
        return;
      }

      initializationInProgress.current = true;
      setLoading(true);

      try {
        console.log('Loading questions...');
        const qs = await getQuestions('heart');
        console.log('Questions loaded:', qs);
        
        if (qs && qs.length > 0) {
          console.log('Setting questions state and flags...');
          setQuestions(qs);
          setIsQuestionsReady(true);
          questionsLoaded.current = true;
          setIsInitialized(true);
          
          console.log('Initialization state:', {
            questionsLoaded: questionsLoaded.current,
            isInitialized: true,
            questionsLength: qs.length,
            currentStep
          });
          
          // Initialize speech recognition after questions are loaded
          console.log('Initializing speech recognition...');
          initializeSpeechRecognition();
          
          // Process any pending voice submission
          if (pendingVoiceSubmission.current) {
            console.log('Processing pending voice submission:', pendingVoiceSubmission.current);
            processVoiceSubmission(pendingVoiceSubmission.current);
            pendingVoiceSubmission.current = null;
          }
        } else {
          console.error('No questions received');
          setFeedback('Error loading questions. Please refresh the page.');
        }
      } catch (error) {
        console.error('Error loading questions:', error);
        setFeedback('Error loading questions. Please refresh the page.');
      } finally {
        setLoading(false);
        initializationInProgress.current = false;
      }
    };

    initialize();

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []); // Empty dependency array to run only once

  const handleTextSubmit = async () => {
    if (!userInput.trim()) return;
    const question = questions[currentStep];
    
    // Get evaluation config and evaluate answer
    const config = getHeartLessonEvaluationConfig(question.id);
    if (!config) {
      console.error('No evaluation config found for question:', question.id);
      return;
    }
    const evaluation = evaluateAnswer(question.id, userInput, config);
    setFeedback(evaluation.feedback);
    setAnswerResult({ correct: evaluation.correct, text: userInput });
    setUserInput('');
  };

  const handleVoiceSubmit = async () => {
    const voiceText = pendingVoiceText.trim();
    if (!voiceText) {
      setFeedback('No speech detected. Please try again.');
      return;
    }

    console.log('Handling voice submit:', voiceText);
    console.log('Voice submission state:', {
      questionsLoaded: questionsLoaded.current,
      questionsLength: questions.length,
      currentStep,
      hasCurrentQuestion: questions[currentStep] ? true : false,
      isInitialized
    });

    if (!questionsLoaded.current || !questions.length || !questions[currentStep]) {
      console.log('Questions not ready, storing voice submission for later');
      pendingVoiceSubmission.current = voiceText;
      return;
    }

    await processVoiceSubmission(voiceText);
    setPendingVoiceText('');
  };

  const startListening = () => {
    console.log('Start listening state:', {
      isInitialized,
      questionsLoaded: questionsLoaded.current,
      questionsLength: questions.length,
      currentStep
    });

    if (!isInitialized || !questionsLoaded.current) {
      setFeedback('Please wait while the lesson is initializing...');
      return;
    }

    if (!recognitionRef.current) {
      setFeedback('Voice recognition is not supported in your browser.');
      return;
    }

    try {
      recognitionRef.current.start();
    } catch (error) {
      console.error('Error starting recognition:', error);
      setFeedback('Error starting voice recognition. Please try again.');
    }
  };

  const handleShowHint = async () => {
    const question = questions[currentStep];
    const res = await getHints(question.id, SESSION_ID);
    setHints(res.hints);
    setShowHint(true);
  };

  const handleNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(prev => prev + 1);
      setAnswerResult(null);
      setFeedback('');
      setTranscript('');
      // Reset voice recognition
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        setTimeout(() => {
          startListening();
        }, 100);
      }
    } else {
      setShowCompletion(true);
    }
  };

  const prevStep = () => {
    setCurrentStep(s => Math.max(s - 1, 0));
    setFeedback('');
    setAnswerResult(null);
    setShowHint(false);
    setHints([]);
    setTranscript('');
    setUserInput('');
    finalTranscriptRef.current = '';
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
  };

  if (loading) return <div>Loading...</div>;
  if (!questions.length) return <div>No questions found.</div>;
  const question = questions[currentStep];

  return (
    <PageContainer>
      <NavBar>
        <NavButton onClick={prevStep} disabled={currentStep === 0}>
          <ArrowLeft size={20} /> Previous
        </NavButton>
        <LogoContainer>
          <Book size={20} />
          <span>Heart Lesson</span>
        </LogoContainer>
        <NavButton onClick={handleNext} disabled={currentStep === questions.length - 1}>
          Next <ArrowRight size={20} />
        </NavButton>
      </NavBar>
      <MainContent>
        <ContentContainer>
          <SectionTitle>{question.category}</SectionTitle>
          <LessonContainer>
            {/* Interactive Heart Model */}
            {showModel && (
              <div style={{ 
                width: '100%', 
                height: '400px', 
                marginBottom: '2rem',
                backgroundColor: '#f8fafc',
                borderRadius: '0.5rem',
                overflow: 'hidden'
              }}>
                <InteractiveHeartModel />
              </div>
            )}
            <QuestionText>{question.text}</QuestionText>
            {/* Text input */}
            <VoiceInputContainer>
              <input
                type="text"
                value={userInput}
                onChange={e => setUserInput(e.target.value)}
                placeholder="Type your answer"
                style={{ flex: 1, border: '1px solid #DDD6FE', borderRadius: '0.5rem', padding: '0.75rem 1rem', fontSize: '1rem', marginRight: '0.5rem' }}
                onKeyDown={e => { if (e.key === 'Enter') handleTextSubmit(); }}
              />
              <AnswerButton onClick={handleTextSubmit}>Submit</AnswerButton>
            </VoiceInputContainer>
            {/* Voice input */}
            <VoiceInputContainer>
              <MicButton onClick={startListening}>
                <MicIcon $isListening={isListening}>
                  <Mic size={24} />
                </MicIcon>
              </MicButton>
              <VoiceInputButton onClick={startListening}>
                {isListening ? 'Listening...' : 'Tap to Speak'}
              </VoiceInputButton>
              {pendingVoiceText && (
                <AnswerButton onClick={handleVoiceSubmit}>Submit Voice</AnswerButton>
              )}
            </VoiceInputContainer>
            {/* Transcript display */}
            {transcript && (
              <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#F5F3FF', borderRadius: '0.5rem' }}>
                <p style={{ margin: 0, color: '#4B5563' }}>You said: {transcript}</p>
              </div>
            )}
            {/* Feedback */}
            {feedback && <FeedbackText>{feedback}</FeedbackText>}
            {/* Answer result */}
            {answerResult && (
              <ResponseCard>
                <ResponseHeader>
                  <CheckIconWrapper>
                    {answerResult.correct ? <Check size={16} /> : <X size={16} />}
                  </CheckIconWrapper>
                </ResponseHeader>
                <ResponseContent>
                  <p>"{answerResult.text}"</p>
                  <p>{answerResult.correct ? 'Correct! Well done!' : 'Not quite right. Try again!'}</p>
                </ResponseContent>
              </ResponseCard>
            )}
            {/* Hint */}
            <HintButton onClick={handleShowHint}>
              <Info size={16} /> Need a hint?
            </HintButton>
            {showHint && hints.length > 0 && (
              <HintContainer>
                <p>{hints[0].text}</p>
              </HintContainer>
            )}
          </LessonContainer>
        </ContentContainer>
      </MainContent>
      <ProgressBarContainer>
        <ProgressBarWrapper>
          <ProgressBarHeader>
            <span>Progress</span>
            <span>
              Step {currentStep + 1} of {questions.length}
            </span>
          </ProgressBarHeader>
          <ProgressBarTrack>
            <ProgressBarIndicator style={{ width: `${((currentStep + 1) / questions.length) * 100}%` }} />
          </ProgressBarTrack>
        </ProgressBarWrapper>
      </ProgressBarContainer>
    </PageContainer>
  );
};

export default HeartLesson; 